/* Photography for the public landing pages (the Level 7 pricing set and the
   human-touch landings that followed it).
   ---------------------------------------------------------------------------
   Licensed via the Unsplash License (commercial use, no attribution required).

   EVERY image below has been downloaded and looked at, and only images that suit
   a UAE accounting / corporate-services audience are kept. That step is not
   optional: the ids recovered from the Level 7 mockup filenames included a
   lingerie fashion shoot sitting under "a real accountant on your side", and a
   beach-camel tourist shot captioned "Priced for the UAE, filed with the FTA".
   An HTTP 200 and a content-type of image/jpeg tell you a file exists — they
   tell you nothing whatsoever about what is in it.

   If you add an image here, download it and view it first. No exceptions.

   Sized and cropped by URL parameters so we ship one request per image at the
   size we actually render, instead of a multi-megabyte original. */

const UNSPLASH = 'https://images.unsplash.com';
const wide = (id: string) => `${UNSPLASH}/${id}?w=1400&q=80&auto=format&fit=crop`;
/** The 21:7 photo bands. Ask the server for the letterbox instead of letting
    `object-fit: cover` centre-crop a 3:2 original — a sky-heavy skyline shot
    centre-crops to an empty band, so `crop=bottom` keeps the towers in frame. */
const band = (id: string) => `${UNSPLASH}/${id}?w=1400&h=467&q=80&auto=format&fit=crop&crop=bottom`;
/** Card-sized crop: the pillar thumbnails render ~360px wide, so 1400 is waste. */
const card = (id: string) => `${UNSPLASH}/${id}?w=760&q=80&auto=format&fit=crop`;
/** A named client, cropped to the face. The client-story slot is a ~5:3 letterbox on
    desktop and a 4:3 on phones, so the source asks for that ratio centred on the
    person rather than the photographer's original framing — otherwise `object-fit:
    cover` crops the head off. Note `crop=faces` and not `fit=facearea`: facearea
    clamps to the detected face and loses the shoulders. */
const person = (id: string) => `${UNSPLASH}/${id}?w=1200&h=720&q=80&auto=format&fit=crop&crop=faces`;
/** The home hero's photo frame is a ~430x470 slot, so a landscape source has to
    be re-cropped to that shape at the source: `object-fit: cover` on a 3:2
    original crops the sides evenly and takes heads off the top. */
const heroFrame = (id: string) => `${UNSPLASH}/${id}?w=900&h=940&q=80&auto=format&fit=crop&crop=faces`;
/** The 52px byline chip beside a quote. `facearea` is the only fit that reliably
    lands a face in a square this small. */
const faceChip = (id: string) => `${UNSPLASH}/${id}?w=160&h=160&q=80&auto=format&fit=facearea&facepad=2.6`;

/* Hoisted because it is both the hero's first frame and its own entry below, and
   the id belongs in one place. */
const ADVISOR_PORTRAIT = heroFrame('photo-1611432579402-7037e3e2c1e4');

export const L7_MEDIA = {
  /* ── The two clients we quote by name ───────────────────────────────────
     A client story is one person saying one thing, so it gets that person's
     face — large in the card, and again as the byline chip. Anonymous stock
     ("suited torso holds tablet", "colleagues high-five over a laptop") is
     what made these sections read as decoration instead of evidence. */

  /** Lara B., content creator, Dubai — quoted on / and /accounting. */
  laraPortrait: person('photo-1662850886700-4ec19bd30d11'),
  laraFace: faceChip('photo-1662850886700-4ec19bd30d11'),

  /** Youssef A., SaaS founder, Dubai mainland — quoted on /corporate. */
  youssefPortrait: person('photo-1705645930353-0e335311ef20'),
  youssefFace: faceChip('photo-1705645930353-0e335311ef20'),

  /** Home hero (/) — an adviser in a yellow blazer holding a tablet, on a plain
      warm backdrop. Plain on purpose: the hero's floating status cards sit over
      all four corners of this frame, so a busy background turns to noise. */
  advisorPortrait: ADVISOR_PORTRAIT,

  /** The frames the home hero cycles through, in order (see HeroPhoto). All
      three are images already vetted in this file, and all three were looked at
      AGAIN in this exact portrait crop before being put here — `proReview` was
      dropped for showing the back of someone's head at this ratio, and the two
      named-client portraits are deliberately absent: the hero's floating card
      calls the person on screen "your accountant", and Lara is quoted as a
      client further down the very same page.

      They read as a sequence rather than three unrelated photos: the adviser,
      the conversation, then the paperwork signed off. */
  homeHeroFrames: [
    ADVISOR_PORTRAIT,                               // the anchor frame
    heroFrame('photo-1713947506827-c646da3ad1db'),  // = accountantMeeting, portrait-cropped
    heroFrame('photo-1681505526188-b05e68c77582'),  // = uaeDocuments, portrait-cropped
  ],

  /** /corporate hero — an adviser walking a client through a file on a tablet,
      warm brick and daylight. Calm and centre-weighted on purpose: the hero's
      floating status cards sit over all four corners of this frame. Cropped to the
      hero slot at the source, like every other photo that lands in it. */
  corporateHero: heroFrame('photo-1551836022-4c4c79ecde51'),

  /** Business Bay and Downtown Dubai on a clear day, the Burj Khalifa behind —
      /corporate/pricing band. Bottom-weighted (see `band`): the original is two
      thirds sky, and the towers are the half the band is there to show. */
  dubaiBusinessBay: band('photo-1758698848798-595ec76587e3'),

  /** Downtown Dubai after dark seen across the water, the Burj Khalifa at the
      centre and the lit waterfront below it — /how-it-works band on conncct.com.
      Replaces a monochrome perforated facade (the Mosque of Mohamed Abdulkhaliq
      Gargash) that the caption beside it called "Dubai's business district": it
      was neither the business district nor anything to do with "Deep GCC
      execution". Cool blue throughout, which is what lets the band's navy caption
      gradient sit on it instead of fighting it, and bottom-weighted (see `band`)
      so the towers and their reflection fill the letterbox rather than sky. */
  dubaiSkylineNight: band('photo-1623638498061-2fcab5587cb0'),

  /** Dubai's financial district — Emirates Towers with the Burj Khalifa behind.
      Used for the UAE bands on the pricing pages and How It Works. */
  dubaiBusinessDistrict: wide('photo-1600068485426-4d32cd411697'),

  /** Aerial coastline — /contact hero. */
  dubaiSkyline: wide('photo-1518684079-3c830dcef090'),

  /** Downtown Dubai by day — home-page video poster. */
  dubaiDowntown: wide('photo-1580674684081-7617fbf3d745'),

  /** Three colleagues laughing over laptops at a wooden table — /careers hero.
      All three faces sit in the middle band of the frame, which is the only part
      that survives the hero's 21:9 crop. */
  teamAtWork: wide('photo-1522202176988-66273c2fd55f'),

  /** A team working a decision out at a pinboard — /careers values. "The
      principles we hire and promote by" needs people deciding something; the
      overhead desk flat-lay it replaces showed a laptop and a coffee. */
  teamDeciding: wide('photo-1681949270990-cecd4728d2e0'),

  /** Glass-walled offices in the evening — /careers life here. */
  teamSession: wide('photo-1541746972996-4e0b0f43e02a'),

  /* ── Section photography, connectcorporate.com ──────────────────────────
     One image per section heading, chosen to show what that heading claims
     rather than to decorate it. All fifteen were downloaded and viewed first
     (see the note at the top of this file) — which is how a US IRS "Tax
     Withholding" form and a Madrid holiday-passport shot got dropped from the
     shortlist for the tax and visa sections. */

  /** A printed management report held up over an open ledger, calculator and
      laptop — "Books, VAT & Corporate Tax" (/). Its amber charts sit with the
      card's yellow accent, and the page is a report rather than a foreign tax
      form (the US 1040 shots that dominate this search were all rejected). */
  booksDesk: card('photo-1711097383282-28097ae16b1d'),

  /** Two people signing paperwork across a bright table — "Protected as you grow" (/). */
  legalSigning: card('photo-1562564055-71e051d33c19'),

  /** Burj Khalifa above a palm-lined boulevard — "Set up & run your company" (/). */
  dubaiBoulevard: card('photo-1703866367063-71eae5acbf5d'),

  /** Downtown skyline under dusk storm light — "Local rules. Local deadlines." (/accounting). */
  dubaiSkylineDusk: wide('photo-1651467606797-e1c660cf3fda'),

  /** An adviser and a founder talking something through over a notebook, laptop
      open between them — the human-touch band (/accounting) and the home hero's
      second frame. Brick, tan and one teal mug, so it sits with the cream page
      instead of the blue-grey glass every other "consultation" stock shot is
      photographed in; both faces are in the middle band, which is the only part
      the 21:7 band keeps. Replaces a cooler meeting shot at Khaled's request. */
  accountantMeeting: wide('photo-1713947506827-c646da3ad1db'),

  /** Two colleagues mid-review at a laptop — the free compliance check (/accounting/pricing). */
  adviserMeeting: wide('photo-1551836022-d5d88e9218df'),

  /** Pins marching up to a circled date — the reminders that come before a
      deadline (/accounting/pricing). Replaces a planner shot with "2019"
      printed across it, which dated the page every time someone read it. */
  filingPlanner: wide('photo-1633526543814-9718c8922b7a'),

  /** Four colleagues working something out over one table — "One team for everything
      it takes" (/corporate). The wide boardroom shot it replaces put two faces
      directly under the band's caption, and at band size its black wall screen and
      draped ceiling read as clutter rather than as a room. Here the people sit right
      of centre and the left third is a shoulder, which is exactly what the caption
      gradient wants; the wood and the amber knit sit with the page's orange. */
  teamOneTable: wide('photo-1758873269317-51888e824b28'),

  /** Suit and kandura shaking hands over a finished licence file, the stamped
      pages still open on the desk — "Local rules. Local desks." (/corporate) and
      the home hero's third frame. The sign-off rather than the reading-through,
      which is what both captions actually claim. Same shoot as the frame it
      replaces (there is no second free Gulf-office set on Unsplash worth using —
      everything else that search returns is either premium or a thobe on a beach),
      so it was viewed in BOTH crops to confirm it reads as a different photo: the
      leaf that ran down the old portrait crop is a corner blur here, and the
      subject is two hands and a document rather than two men at a table. */
  uaeDocuments: wide('photo-1681505526188-b05e68c77582'),

  /** Two specialists reviewing a file and a laptop — the human-touch band (/corporate). */
  proReview: wide('photo-1626105985445-6430a31f6f96'),

  /** Glass-walled private office, desk and chairs — "Or build a custom setup" (/corporate/pricing). */
  officeGlass: wide('photo-1705909773171-4ba952b9c0af'),

  /** Downtown Dubai at golden hour, people on the boulevard — /corporate/pricing close. */
  downtownDubaiWalk: wide('photo-1617449512807-7401d38e5c29'),

  /** Hands mapping a plan out on a whiteboard — "Six stages. No guesswork." (/how-it-works). */
  planningBoard: wide('photo-1532622785990-d2c36a76f5a6'),

  /** Two colleagues moving cards on a board — "What you do, and what we handle" (/how-it-works). */
  kanbanSession: wide('photo-1677506050775-18ac86b9c2c0'),
} as const;

/* ── Home-page video ───────────────────────────────────────────────────────
   TO TURN THE SECTION ON: paste the video URL into `url` below. That's the only
   change needed — a YouTube, Vimeo or direct .mp4 link all work, and the section
   appears on the home page immediately.

   It stays hidden while `url` is empty on purpose: a play button with nothing
   behind it is worse than no section at all. There is no Conncct video to point
   at yet (the YouTube channel @Conncct has no uploads, and the Level 7 mockups'
   "▶" turned out to be a decorative glyph on a section label, not a real embed). */
export const L7_VIDEO = {
  /* "A woman discussing to the team" — chosen by Khaled, so this one IS content-checked.
     Pexels License: free commercial use, no attribution, no release needed.
     1080p rendition rather than the 4K original — this is a landing page.
     https://www.pexels.com/video/a-woman-discussing-to-the-team-7792186/ */
  url: 'https://videos.pexels.com/video-files/7792186/7792186-hd_1920_1080_25fps.mp4',
  poster: L7_MEDIA.dubaiDowntown,
  eyebrow: 'Watch',
  title: 'See how it actually works.',
  lead: 'Two minutes on what we take off your desk, who does it, and what it costs.',
} as const;
